import { useEffect, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AlertDescription,
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@sportscanner/ui";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/responsive-dialog";
import { courtBaseSchema } from "@sportscanner/schemas";
import { apiGet, apiPost, apiPut, getApiErrorCode } from "@/lib/api";
import type { MeCourt, MeSport } from "@/lib/me";
import {
  surfacesForSport,
  type SurfaceKey,
  sizesForSport,
  type SizeKey,
} from "@/lib/sports";
import BookingImpactDialog from "@/components/booking-impact-dialog";

type Mode = "create" | "edit";

interface Props {
  mode: Mode;
  clubId: string;
  sports: MeSport[];
  // Free clubs don't charge — the price input is hidden and the existing
  // (or 0) price is submitted unchanged.
  isFree: boolean;
  defaultWorkingHoursStart: string;
  defaultWorkingHoursEnd: string;
  court?: MeCourt;
  // The editing court's two half-courts (basketball split), if any — used to
  // pre-fill the half price + names. Empty/undefined for non-split courts.
  halves?: MeCourt[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => Promise<void> | void;
}

export default function CourtFormDialog({
  mode,
  clubId,
  sports,
  isFree,
  defaultWorkingHoursStart,
  defaultWorkingHoursEnd,
  court,
  halves,
  open,
  onOpenChange,
  onSaved,
}: Props) {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<"basic" | "more">("basic");

  const [sportId, setSportId] = useState("");
  const [name, setName] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [nameRu, setNameRu] = useState("");
  const [isIndoor, setIsIndoor] = useState(false);
  const [surface, setSurface] = useState<SurfaceKey | "">("");
  const [size, setSize] = useState<SizeKey | "">("");
  const [priceInput, setPriceInput] = useState("");
  // Basketball "two halves" split. Whole court stays bookable; each half is
  // priced at `halfPriceInput` and bookable independently.
  const [splittable, setSplittable] = useState(false);
  const [halfPriceInput, setHalfPriceInput] = useState("");
  const [halfNameLeft, setHalfNameLeft] = useState("");
  const [halfNameRight, setHalfNameRight] = useState("");
  const [overrideHours, setOverrideHours] = useState(false);
  const [whStart, setWhStart] = useState(defaultWorkingHoursStart);
  const [whEnd, setWhEnd] = useState(defaultWorkingHoursEnd);
  const [isActive, setIsActive] = useState(true);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  type ImpactState =
    | { kind: "court-sport-block"; count: number }
    | { kind: "court-unsplit-block"; count: number }
    | { kind: "court-hide-warn"; count: number }
    | { kind: "court-hours-warn"; count: number };
  const [impact, setImpact] = useState<ImpactState | null>(null);
  const [preflighting, setPreflighting] = useState(false);

  const selectedSportName = sports.find((s) => s.id === sportId)?.name;
  const surfaceOptions = surfacesForSport(selectedSportName);
  const sizeOptions = sizesForSport(selectedSportName);
  // Splitting into halves is basketball-only. `effectiveSplittable` is what we
  // actually submit — the toggle can't keep split on for a non-basketball sport.
  const isBasketball = selectedSportName === "BASKETBALL";
  const effectiveSplittable = isBasketball && splittable;
  // Current half price + names (from the existing halves) for dirty-checking.
  const sortedHalves = [...(halves ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const currentHalfPrice = sortedHalves[0]
    ? Number(sortedHalves[0].pricePerSession)
    : NaN;
  const currentHalfNameLeft = sortedHalves[0]?.name ?? "";
  const currentHalfNameRight = sortedHalves[1]?.name ?? "";

  // Reset form whenever the dialog opens or the editing target changes.
  useEffect(() => {
    if (!open) return;
    setActiveTab("basic");
    if (mode === "edit" && court) {
      setSportId(court.sportId);
      setName(court.name);
      setNameEn(court.nameEn ?? "");
      setNameRu(court.nameRu ?? "");
      setIsIndoor(court.isIndoor);
      setSurface((court.surface as SurfaceKey | null) ?? "");
      setSize((court.size as SizeKey | null) ?? "");
      setPriceInput(String(Number(court.pricePerSession)));
      setSplittable(court.isSplittable);
      // Pre-fill the half price + names from the court's two halves (sorted by
      // name for a stable left/right). Both share the same price.
      {
        const sortedHalves = [...(halves ?? [])].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        setHalfPriceInput(
          sortedHalves[0]
            ? String(Number(sortedHalves[0].pricePerSession))
            : "",
        );
        setHalfNameLeft(sortedHalves[0]?.name ?? "");
        setHalfNameRight(sortedHalves[1]?.name ?? "");
      }
      const hasOverride = !!court.workingHoursStart && !!court.workingHoursEnd;
      setOverrideHours(hasOverride);
      setWhStart(court.workingHoursStart || defaultWorkingHoursStart);
      setWhEnd(court.workingHoursEnd || defaultWorkingHoursEnd);
      setIsActive(court.isActive);
    } else {
      setSportId(sports[0]?.id ?? "");
      setName("");
      setNameEn("");
      setNameRu("");
      setIsIndoor(false);
      setSurface("");
      setSize("");
      setPriceInput("");
      setSplittable(false);
      setHalfPriceInput("");
      setHalfNameLeft("");
      setHalfNameRight("");
      setOverrideHours(false);
      setWhStart(defaultWorkingHoursStart);
      setWhEnd(defaultWorkingHoursEnd);
      setIsActive(true);
    }
    setErrorCode(null);
    setImpact(null);
  }, [
    open,
    mode,
    court,
    halves,
    sports,
    defaultWorkingHoursStart,
    defaultWorkingHoursEnd,
  ]);

  const priceNumber = priceInput === "" ? NaN : Number(priceInput);
  // For free clubs the input is hidden; submit the existing price (edit) or 0
  // (create), and skip price validation entirely.
  const submittedPrice = Number.isFinite(priceNumber) ? priceNumber : 0;
  const priceValid =
    isFree ||
    (Number.isFinite(priceNumber) &&
      priceNumber >= 0 &&
      priceNumber <= 1_000_000);

  // Half price mirrors the court price rules; only required when split is on
  // and the club charges.
  const halfPriceNumber = halfPriceInput === "" ? NaN : Number(halfPriceInput);
  const submittedHalfPrice = Number.isFinite(halfPriceNumber)
    ? halfPriceNumber
    : 0;
  const halfPriceValid =
    !effectiveSplittable ||
    isFree ||
    (Number.isFinite(halfPriceNumber) &&
      halfPriceNumber >= 0 &&
      halfPriceNumber <= 1_000_000);

  const isValid =
    !!sportId &&
    name.trim().length >= 1 &&
    priceValid &&
    halfPriceValid &&
    (!overrideHours || whStart < whEnd);

  // Edit mode pre-fills the form, so isValid alone would let users save without
  // changing anything. Gate Save on at least one field differing from the
  // current court. Create mode has no "before" state, so isValid already
  // covers it — there's nothing meaningful to compare against.
  const isDirty =
    mode === "edit" && court
      ? sportId !== court.sportId ||
        name.trim() !== court.name ||
        nameEn.trim() !== (court.nameEn ?? "") ||
        nameRu.trim() !== (court.nameRu ?? "") ||
        isIndoor !== court.isIndoor ||
        (surface || null) !== (court.surface || null) ||
        (size || null) !== (court.size || null) ||
        priceNumber !== Number(court.pricePerSession) ||
        overrideHours !==
          (!!court.workingHoursStart && !!court.workingHoursEnd) ||
        (overrideHours &&
          (whStart !== (court.workingHoursStart || defaultWorkingHoursStart) ||
            whEnd !== (court.workingHoursEnd || defaultWorkingHoursEnd))) ||
        isActive !== court.isActive ||
        effectiveSplittable !== court.isSplittable ||
        (effectiveSplittable &&
          (submittedHalfPrice !== currentHalfPrice ||
            halfNameLeft.trim() !== currentHalfNameLeft ||
            halfNameRight.trim() !== currentHalfNameRight))
      : true;

  const mutation = useMutation({
    mutationFn: async () => {
      const parsed = courtBaseSchema.safeParse({
        sportId,
        name: name.trim(),
        nameEn: nameEn.trim() || null,
        nameRu: nameRu.trim() || null,
        isIndoor,
        surface: surface || null,
        size: size || null,
        pricePerSession: submittedPrice,
        workingHoursStart: overrideHours ? whStart : null,
        workingHoursEnd: overrideHours ? whEnd : null,
        ...(mode === "edit" ? { isActive } : {}),
        isSplittable: effectiveSplittable,
        ...(effectiveSplittable
          ? {
              halfPrice: submittedHalfPrice,
              halfNameLeft: halfNameLeft.trim() || null,
              halfNameRight: halfNameRight.trim() || null,
            }
          : {}),
      });
      if (!parsed.success) {
        throw { error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED" };
      }
      if (mode === "edit" && court) {
        return apiPut(
          `/api/admin/clubs/${clubId}/courts/${court.id}`,
          parsed.data,
        );
      }
      return apiPost(`/api/admin/clubs/${clubId}/courts`, parsed.data);
    },
    onSuccess: async () => {
      setErrorCode(null);
      setImpact(null);
      onOpenChange(false);
      await onSaved();
    },
    onError: (err) => {
      setErrorCode(getApiErrorCode(err));
      setImpact(null);
    },
  });

  // Preflight every edit that could affect existing bookings, in priority
  // order: block (sport change with different duration) → warn (hide) → warn
  // (hours narrowing). Create mode has no "before" state, so nothing to
  // preflight. The server-side guard still backstops block cases.
  async function handleSaveClick() {
    if (!isValid || !isDirty || mutation.isPending || preflighting) return;
    if (mode !== "edit" || !court) {
      mutation.mutate();
      return;
    }

    const sportChanged = sportId !== court.sportId;
    const unsplitRequested = court.isSplittable && !effectiveSplittable;
    const hideRequested = court.isActive && !isActive;
    const hadOverride = !!court.workingHoursStart && !!court.workingHoursEnd;
    const oldStart = court.workingHoursStart ?? defaultWorkingHoursStart;
    const oldEnd = court.workingHoursEnd ?? defaultWorkingHoursEnd;
    const newStart = overrideHours ? whStart : defaultWorkingHoursStart;
    const newEnd = overrideHours ? whEnd : defaultWorkingHoursEnd;
    // Narrowing = the new effective window is strictly inside the old one.
    // Widening (or no change) needs no warning. We only preflight the court
    // when the user is actively setting / keeping an override; turning the
    // override off widens to the club default and never narrows.
    const hoursNarrowed =
      overrideHours &&
      (hadOverride
        ? newStart > oldStart || newEnd < oldEnd
        : newStart > oldStart || newEnd < oldEnd);

    setPreflighting(true);
    try {
      if (sportChanged) {
        const params = new URLSearchParams({
          kind: "court-sport",
          courtId: court.id,
          sportId,
        });
        const res = await apiGet<{ severity: string; count: number }>(
          `/api/admin/clubs/${clubId}/edit-impact?${params.toString()}`,
        );
        if (res.severity === "block") {
          setImpact({ kind: "court-sport-block", count: res.count });
          return;
        }
      }

      if (unsplitRequested) {
        const params = new URLSearchParams({
          kind: "court-unsplit",
          courtId: court.id,
        });
        const res = await apiGet<{ severity: string; count: number }>(
          `/api/admin/clubs/${clubId}/edit-impact?${params.toString()}`,
        );
        if (res.severity === "block") {
          setImpact({ kind: "court-unsplit-block", count: res.count });
          return;
        }
      }

      if (hideRequested) {
        const params = new URLSearchParams({
          kind: "court-hide",
          courtId: court.id,
        });
        const res = await apiGet<{ severity: string; count: number }>(
          `/api/admin/clubs/${clubId}/edit-impact?${params.toString()}`,
        );
        if (res.severity === "warn") {
          setImpact({ kind: "court-hide-warn", count: res.count });
          return;
        }
      }

      if (hoursNarrowed) {
        const params = new URLSearchParams({
          kind: "court-hours",
          courtId: court.id,
          workingHoursStart: newStart,
          workingHoursEnd: newEnd,
        });
        const res = await apiGet<{ severity: string; count: number }>(
          `/api/admin/clubs/${clubId}/edit-impact?${params.toString()}`,
        );
        if (res.severity === "warn") {
          setImpact({ kind: "court-hours-warn", count: res.count });
          return;
        }
      }

      mutation.mutate();
    } catch (err) {
      // Preflight network failure shouldn't strand the user; try the save —
      // server-side guard still rejects block cases with 409.
      setErrorCode(getApiErrorCode(err));
      mutation.mutate();
    } finally {
      setPreflighting(false);
    }
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="md:max-h-[90vh] md:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "edit"
              ? t("courts.editCourt")
              : t("onboarding.courts.addCourt")}
          </DialogTitle>
          <DialogDescription>
            {t("onboarding.courts.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {errorCode && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t(`errors.api.${errorCode}`)}
              </AlertDescription>
            </Alert>
          )}

          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as "basic" | "more")}
          >
            <TabsList className="w-full">
              <TabsTrigger value="basic">
                {t("courts.dialog.tabs.basic")}
              </TabsTrigger>
              <TabsTrigger value="more">
                {t("courts.dialog.tabs.more")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-sm text-neutral-700">
                  {t("onboarding.courts.sportLabel")}{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={sportId}
                  onValueChange={(v) => {
                    setSportId(v);
                    // Surface and size are per-sport; clear when switching sport
                    // so we don't submit e.g. CLAY for a basketball court or a
                    // football size for tennis. Split is basketball-only, so a
                    // sport change resets it too.
                    setSurface("");
                    setSize("");
                    setSplittable(false);
                  }}
                  disabled={mutation.isPending}
                >
                  <SelectTrigger className="rounded-lg border-neutral-200 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sports.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {t(`sports.${s.name}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="court-name"
                  className="text-sm text-neutral-700"
                >
                  {t("onboarding.courts.nameLabel")}{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="court-name"
                  type="text"
                  placeholder={t("onboarding.courts.namePlaceholder")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={mutation.isPending}
                  className="rounded-lg border-neutral-200"
                />
                <p className="text-xs text-neutral-500">
                  {t("onboarding.courts.nameHelp")}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-sm text-neutral-700">
                  {t("onboarding.courts.indoorLabel")}
                </Label>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs ${
                      !isIndoor
                        ? "text-neutral-800 font-medium"
                        : "text-neutral-400"
                    }`}
                  >
                    {t("onboarding.courts.outdoor")}
                  </span>
                  <Switch
                    checked={isIndoor}
                    onCheckedChange={setIsIndoor}
                    disabled={mutation.isPending}
                  />
                  <span
                    className={`text-xs ${
                      isIndoor
                        ? "text-neutral-800 font-medium"
                        : "text-neutral-400"
                    }`}
                  >
                    {t("onboarding.courts.indoor")}
                  </span>
                </div>
              </div>

              {!isFree && (
                <div className="space-y-2">
                  <Label
                    htmlFor="court-price"
                    className="text-sm text-neutral-700"
                  >
                    {t("onboarding.courts.priceLabel")}{" "}
                    <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="court-price"
                      type="number"
                      inputMode="numeric"
                      min={0}
                      step={50}
                      placeholder="0"
                      value={priceInput}
                      onChange={(e) => setPriceInput(e.target.value)}
                      disabled={mutation.isPending}
                      className="rounded-lg border-neutral-200 w-40"
                    />
                    <span className="text-sm text-neutral-500">RSD</span>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-neutral-700">
                    {t("onboarding.courts.workingHoursLabel")}
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">
                      {t("courts.row.overrideHours")}
                    </span>
                    <Switch
                      checked={overrideHours}
                      onCheckedChange={setOverrideHours}
                      disabled={mutation.isPending}
                    />
                  </div>
                </div>
                {overrideHours && (
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Input
                      type="time"
                      value={whStart}
                      onChange={(e) => setWhStart(e.target.value)}
                      disabled={mutation.isPending}
                      className="rounded-lg border-neutral-200 flex-1 sm:flex-initial sm:w-32"
                    />
                    <span className="text-neutral-400">—</span>
                    <Input
                      type="time"
                      value={whEnd}
                      onChange={(e) => setWhEnd(e.target.value)}
                      disabled={mutation.isPending}
                      className="rounded-lg border-neutral-200 flex-1 sm:flex-initial sm:w-32"
                    />
                  </div>
                )}
                <p className="text-xs text-neutral-500">
                  {t("onboarding.courts.workingHoursHelp")}
                </p>
              </div>

              {mode === "edit" && (
                <div className="flex items-center justify-between border-t border-neutral-100 pt-4">
                  <Label className="text-sm text-neutral-700">
                    {t("courts.row.activeToggle")}
                  </Label>
                  <Switch
                    checked={isActive}
                    onCheckedChange={setIsActive}
                    disabled={mutation.isPending}
                  />
                </div>
              )}
            </TabsContent>

            <TabsContent value="more" className="space-y-4 mt-4">
              {surfaceOptions.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">
                    {t("onboarding.courts.surfaceLabel")}{" "}
                    <span className="text-neutral-400 text-xs">
                      {t("common.optional")}
                    </span>
                  </Label>
                  <Select
                    value={surface || "__none__"}
                    onValueChange={(v) =>
                      setSurface(v === "__none__" ? "" : (v as SurfaceKey))
                    }
                    disabled={mutation.isPending}
                  >
                    <SelectTrigger className="rounded-lg border-neutral-200 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        {t("onboarding.courts.surfaceNone")}
                      </SelectItem>
                      {surfaceOptions.map((s) => (
                        <SelectItem key={s} value={s}>
                          {t(`surfaces.${s}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {sizeOptions.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm text-neutral-700">
                    {t("courts.dialog.sizeLabel")}{" "}
                    <span className="text-neutral-400 text-xs">
                      {t("common.optional")}
                    </span>
                  </Label>
                  <Select
                    value={size || "__none__"}
                    onValueChange={(v) =>
                      setSize(v === "__none__" ? "" : (v as SizeKey))
                    }
                    disabled={mutation.isPending}
                  >
                    <SelectTrigger className="rounded-lg border-neutral-200 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        {t("courts.dialog.sizeNone")}
                      </SelectItem>
                      {sizeOptions.map((s) => (
                        <SelectItem key={s} value={s}>
                          {t(`sizes.${s}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {isBasketball && (
                <div className="space-y-3 rounded-lg border border-neutral-200 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium text-neutral-800">
                        {t("courts.dialog.split.label")}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {t("courts.dialog.split.hint")}
                      </p>
                    </div>
                    <Switch
                      checked={splittable}
                      onCheckedChange={setSplittable}
                      disabled={mutation.isPending}
                    />
                  </div>

                  {splittable && (
                    <div className="space-y-3 border-t border-neutral-100 pt-3">
                      {!isFree && (
                        <div className="space-y-2">
                          <Label
                            htmlFor="court-half-price"
                            className="text-sm text-neutral-700"
                          >
                            {t("courts.dialog.split.halfPriceLabel")}{" "}
                            <span className="text-red-500">*</span>
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="court-half-price"
                              type="number"
                              inputMode="numeric"
                              min={0}
                              step={50}
                              placeholder="0"
                              value={halfPriceInput}
                              onChange={(e) =>
                                setHalfPriceInput(e.target.value)
                              }
                              disabled={mutation.isPending}
                              className="rounded-lg border-neutral-200 w-40"
                            />
                            <span className="text-sm text-neutral-500">
                              RSD
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="court-half-left"
                            className="text-xs text-neutral-600"
                          >
                            {t("courts.dialog.split.leftName")}
                          </Label>
                          <Input
                            id="court-half-left"
                            type="text"
                            value={halfNameLeft}
                            onChange={(e) => setHalfNameLeft(e.target.value)}
                            placeholder={t(
                              "courts.dialog.split.leftPlaceholder",
                            )}
                            disabled={mutation.isPending}
                            className="rounded-lg border-neutral-200"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label
                            htmlFor="court-half-right"
                            className="text-xs text-neutral-600"
                          >
                            {t("courts.dialog.split.rightName")}
                          </Label>
                          <Input
                            id="court-half-right"
                            type="text"
                            value={halfNameRight}
                            onChange={(e) => setHalfNameRight(e.target.value)}
                            placeholder={t(
                              "courts.dialog.split.rightPlaceholder",
                            )}
                            disabled={mutation.isPending}
                            className="rounded-lg border-neutral-200"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3 rounded-lg border border-neutral-200 p-3">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium text-neutral-800">
                    {t("courts.dialog.nameTranslations.label")}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {t("courts.dialog.nameTranslations.hint")}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="court-name-en"
                      className="text-xs text-neutral-600"
                    >
                      {t("courts.dialog.nameTranslations.english")}
                    </Label>
                    <Input
                      id="court-name-en"
                      type="text"
                      value={nameEn}
                      onChange={(e) => setNameEn(e.target.value)}
                      disabled={mutation.isPending}
                      className="rounded-lg border-neutral-200"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="court-name-ru"
                      className="text-xs text-neutral-600"
                    >
                      {t("courts.dialog.nameTranslations.russian")}
                    </Label>
                    <Input
                      id="court-name-ru"
                      type="text"
                      value={nameRu}
                      onChange={(e) => setNameRu(e.target.value)}
                      disabled={mutation.isPending}
                      className="rounded-lg border-neutral-200"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSaveClick}
            disabled={
              !isValid || !isDirty || mutation.isPending || preflighting
            }
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {mutation.isPending || preflighting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              t("common.save")
            )}
          </Button>
        </DialogFooter>

        <BookingImpactDialog
          open={impact?.kind === "court-sport-block"}
          onOpenChange={(o) => {
            if (!o) setImpact(null);
          }}
          severity="block"
          count={impact?.kind === "court-sport-block" ? impact.count : 0}
          title={t("bookingImpact.courtSportChange.blockTitle")}
          description={t("bookingImpact.courtSportChange.blockDescription")}
        />
        <BookingImpactDialog
          open={impact?.kind === "court-unsplit-block"}
          onOpenChange={(o) => {
            if (!o) setImpact(null);
          }}
          severity="block"
          count={impact?.kind === "court-unsplit-block" ? impact.count : 0}
          title={t("bookingImpact.courtUnsplit.blockTitle")}
          description={t("bookingImpact.courtUnsplit.blockDescription")}
        />
        <BookingImpactDialog
          open={impact?.kind === "court-hide-warn"}
          onOpenChange={(o) => {
            if (!o) setImpact(null);
          }}
          severity="warn"
          count={impact?.kind === "court-hide-warn" ? impact.count : 0}
          title={t("bookingImpact.courtHide.warnTitle")}
          description={t("bookingImpact.courtHide.warnDescription")}
          confirmLabel={t("bookingImpact.actions.saveAnyway")}
          isPending={mutation.isPending}
          onConfirm={() => mutation.mutate()}
        />
        <BookingImpactDialog
          open={impact?.kind === "court-hours-warn"}
          onOpenChange={(o) => {
            if (!o) setImpact(null);
          }}
          severity="warn"
          count={impact?.kind === "court-hours-warn" ? impact.count : 0}
          title={t("bookingImpact.courtHours.warnTitle")}
          description={t("bookingImpact.courtHours.warnDescription")}
          confirmLabel={t("bookingImpact.actions.saveAnyway")}
          isPending={mutation.isPending}
          onConfirm={() => mutation.mutate()}
        />
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
